import { useState, useEffect } from "react";
import { Plus, Package, Mail, MessageCircle, Download, Barcode, Truck, Trash2, PlusCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupplierOrders({ user }) {
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItem, setNewItem] = useState({ item_code: "", description: "", brand: "", quantity: 1 });
  const [formData, setFormData] = useState({
    quote_id: "",
    supplier_id: "",
    supplier_name: "",
    supplier_email: "",
    supplier_phone: "",
    items: [],
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, quotesRes, suppliersRes] = await Promise.all([
        authFetch(`${API}/supplier-orders`, { }),
        authFetch(`${API}/quotes`, { }),
        authFetch(`${API}/suppliers`, { })
      ]);

      if (ordersRes.ok) setOrders(await ordersRes.json());
      if (quotesRes.ok) setQuotes(await quotesRes.json());
      if (suppliersRes.ok) setSuppliers(await suppliersRes.json());
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const handleQuoteSelect = (quoteId) => {
    const quote = quotes.find(q => q.quote_id === quoteId);
    if (quote) {
      setSelectedQuote(quote);
      setFormData(prev => ({
        ...prev,
        quote_id: quoteId,
        items: quote.items.map(item => ({
          item_code: item.item_code || "",
          description: item.description,
          brand: item.brand,
          quantity: item.quantity
        }))
      }));
    }
  };

  const handleSupplierSelect = (supplierId) => {
    const supplier = suppliers.find(s => s.supplier_id === supplierId);
    if (supplier) {
      setFormData(prev => ({
        ...prev,
        supplier_id: supplierId,
        supplier_name: supplier.name,
        supplier_email: supplier.email,
        supplier_phone: supplier.phone
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await authFetch(`${API}/supplier-orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success("Ordine fornitore creato");
        setShowDialog(false);
        resetForm();
        fetchData();
      } else {
        throw new Error("Errore");
      }
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleStatusChange = async (orderId, status) => {
    try {
      const response = await authFetch(`${API}/supplier-orders/${orderId}/status?status=${status}`, {
        method: "PATCH"
      });

      if (response.ok) {
        toast.success("Stato aggiornato");
        fetchData();
        if (selectedOrder) {
          setSelectedOrder({...selectedOrder, status});
        }
      }
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleDeleteOrder = async (orderId) => {
    try {
      const response = await authFetch(`${API}/supplier-orders/${orderId}`, {
        method: "DELETE"
      });

      if (response.ok) {
        toast.success("Ordine eliminato");
        setDeleteConfirm(null);
        setSelectedOrder(null);
        fetchData();
      } else {
        toast.error("Errore nell'eliminazione");
      }
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleAddManualItem = async () => {
    if (!selectedOrder) return;
    if (!newItem.item_code || !newItem.description || !newItem.brand) {
      toast.error("Codice, Descrizione e Marca sono obbligatori");
      return;
    }

    try {
      const response = await authFetch(`${API}/supplier-orders/${selectedOrder.order_id}/items`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newItem)
      });

      if (response.ok) {
        const updatedOrder = await response.json();
        setSelectedOrder(updatedOrder);
        setNewItem({ item_code: "", description: "", brand: "", quantity: 1 });
        setShowAddItem(false);
        toast.success("Articolo aggiunto");
        fetchData();
      } else {
        toast.error("Errore nell'aggiunta");
      }
    } catch (error) {
      toast.error("Errore nell'aggiunta");
    }
  };

  const handleRemoveItem = async (itemIndex) => {
    if (!selectedOrder) return;

    try {
      const response = await authFetch(`${API}/supplier-orders/${selectedOrder.order_id}/items/${itemIndex}`, {
        method: "DELETE"
      });

      if (response.ok) {
        toast.success("Articolo rimosso");
        // Update local state
        const newItems = [...selectedOrder.items];
        newItems.splice(itemIndex, 1);
        setSelectedOrder({ ...selectedOrder, items: newItems });
        fetchData();
      } else {
        toast.error("Errore nella rimozione");
      }
    } catch (error) {
      toast.error("Errore nella rimozione");
    }
  };

  const resetForm = () => {
    setFormData({
      quote_id: "",
      supplier_id: "",
      supplier_name: "",
      supplier_email: "",
      supplier_phone: "",
      items: [],
      notes: ""
    });
    setSelectedQuote(null);
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { item_code: "", description: "", brand: "", quantity: 1 }]
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? {...item, [field]: value} : item)
    }));
  };

  const exportToPDF = (order) => {
    const doc = new jsPDF();
    const primaryColor = [249, 115, 22];
    const darkColor = [24, 24, 27];
    
    // Header
    doc.setFillColor(...darkColor);
    doc.rect(0, 0, 210, 45, 'F');
    
    // Company name (user's company)
    doc.setTextColor(...primaryColor);
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.text(user?.company_name || "PServicePro", 15, 20);
    
    // Company details
    doc.setTextColor(200, 200, 200);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    if (user?.address) {
      doc.text(`${user.address}, ${user.zip_code} ${user.city} (${user.province})`, 15, 28);
    }
    if (user?.phone) {
      doc.text(`Tel: ${user.phone} | P.IVA: ${user.vat_number || ''}`, 15, 34);
    }
    if (user?.pec_email) {
      doc.text(`PEC: ${user.pec_email}`, 15, 40);
    }
    
    // Order info
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text(`Ordine Fornitore N° ${order.order_number}`, 130, 20);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Data: ${order.created_at?.split("T")[0] || new Date().toISOString().split("T")[0]}`, 130, 28);
    
    // Supplier info
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("DESTINATARIO (Fornitore)", 15, 60);
    
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(order.supplier_name, 15, 68);
    doc.text(`Email: ${order.supplier_email}`, 15, 75);
    if (order.supplier_phone) {
      doc.text(`Tel: ${order.supplier_phone}`, 15, 82);
    }
    
    // Items table (without prices)
    const tableData = order.items.map(item => [
      item.item_code || "-",
      item.description,
      item.brand || "-",
      item.quantity.toString()
    ]);
    
    autoTable(doc, {
      startY: 95,
      head: [['Codice', 'Descrizione', 'Marca', 'Quantità']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: primaryColor, textColor: [0, 0, 0], fontStyle: 'bold' },
      styles: { fontSize: 10, cellPadding: 5 },
      columnStyles: {
        0: { cellWidth: 35 },
        1: { cellWidth: 80 },
        2: { cellWidth: 40 },
        3: { cellWidth: 25, halign: 'center' }
      }
    });
    
    // Notes
    const finalY = doc.lastAutoTable.finalY + 15;
    if (order.notes) {
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("Note:", 15, finalY);
      doc.setFont("helvetica", "normal");
      doc.text(order.notes, 15, finalY + 8, { maxWidth: 180 });
    }
    
    // Footer
    const pageHeight = doc.internal.pageSize.height;
    doc.setFillColor(...darkColor);
    doc.rect(0, pageHeight - 20, 210, 20, 'F');
    doc.setTextColor(150, 150, 150);
    doc.setFontSize(8);
    doc.text("PServicePro - Gestione Officina Professionale", 105, pageHeight - 10, { align: 'center' });
    
    doc.save(`Ordine_${order.order_number}_${order.supplier_name.replace(/\s/g, '_')}.pdf`);
    toast.success("PDF scaricato con successo");
  };

  const sendViaEmail = (order) => {
    const subject = encodeURIComponent(`Ordine #${order.order_number} - PServicePro`);
    const body = encodeURIComponent(
`Gentile ${order.supplier_name},

Vi inviamo l'ordine n. ${order.order_number}.

ARTICOLI ORDINATI:
${order.items.map(item => `- [${item.item_code || 'N/A'}] ${item.description} (${item.brand || 'N/A'}) - Qtà: ${item.quantity}`).join('\n')}

${order.notes ? `Note: ${order.notes}\n` : ''}
Vi preghiamo di confermare la ricezione dell'ordine e i tempi di consegna.

Cordiali saluti,
PServicePro - Gestione Officina`
    );
    
    window.open(`mailto:${order.supplier_email}?subject=${subject}&body=${body}`, '_blank');
    toast.success("Client email aperto");
  };

  const sendViaWhatsApp = (order) => {
    let phone = order.supplier_phone || prompt("Inserisci il numero WhatsApp del fornitore (es. 3331234567):");
    if (!phone) return;
    
    let cleanPhone = phone.replace(/[\s\-\(\)\.]/g, '');
    if (!cleanPhone.startsWith('+') && !cleanPhone.startsWith('39')) {
      cleanPhone = '39' + cleanPhone;
    } else if (cleanPhone.startsWith('+')) {
      cleanPhone = cleanPhone.substring(1);
    }
    
    const message = encodeURIComponent(
`📦 *PServicePro - Ordine Fornitore #${order.order_number}*

Gentile *${order.supplier_name}*,

Vi inviamo il seguente ordine:

📋 *ARTICOLI:*
${order.items.map(item => `• [${item.item_code || '-'}] ${item.description} (${item.brand || '-'}) - Qtà: ${item.quantity}`).join('\n')}

${order.notes ? `📝 Note: ${order.notes}\n` : ''}
Vi preghiamo di confermare disponibilità e tempi di consegna.

_PServicePro - Gestione Officina_`
    );
    
    window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
    toast.success("WhatsApp aperto");
  };

  const statusColors = {
    draft: "status-draft",
    sent: "status-sent",
    confirmed: "status-accepted",
    in_delivery: "status-in_progress",
    ready_for_pickup: "bg-cyan-500/20 text-cyan-400",
    partial_shipped: "bg-orange-500/20 text-orange-400",
    shipped: "status-in_progress",
    received: "status-completed",
    cancelled: "status-cancelled"
  };

  const statusLabels = {
    draft: "Bozza",
    sent: "Inviato",
    confirmed: "Confermato",
    in_delivery: "In Consegna",
    ready_for_pickup: "Pronto per il Ritiro",
    partial_shipped: "Spedito Parziale",
    shipped: "Spedito",
    received: "Ricevuto",
    cancelled: "Annullato"
  };

  // Map ricambista status to display
  const ricambistaStatusLabels = {
    pending: "In Attesa",
    processing: "In Lavorazione",
    ready_for_pickup: "Pronto per il Ritiro",
    partial_shipped: "Spedito Parziale",
    shipped: "Spedito",
    delivered: "Consegnato",
    cancelled: "Annullato"
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="supplier-orders-page" className="space-y-6">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Ordini Fornitore</h1>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button data-testid="new-order-btn" className="btn-primary">
              <Plus className="w-5 h-5 mr-2" />
              Nuovo Ordine
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white uppercase font-['Barlow_Condensed'] text-xl">
                Nuovo Ordine Fornitore
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              {/* Optional: Load from Quote */}
              <div className="form-group">
                <label className="form-label">Carica da Preventivo (opzionale)</label>
                <select
                  className="form-input"
                  value={formData.quote_id}
                  onChange={(e) => handleQuoteSelect(e.target.value)}
                >
                  <option value="">-- Seleziona un preventivo --</option>
                  {quotes.map(quote => (
                    <option key={quote.quote_id} value={quote.quote_id}>
                      #{quote.quote_number} - {quote.customer_name} - {quote.vehicle_plate}
                    </option>
                  ))}
                </select>
              </div>

              {/* Supplier Selection */}
              {suppliers.length > 0 && (
                <div className="form-group">
                  <label className="form-label flex items-center gap-2">
                    <Truck className="w-4 h-4 text-primary" />
                    Seleziona da Anagrafica
                  </label>
                  <select
                    className="form-input"
                    onChange={(e) => handleSupplierSelect(e.target.value)}
                  >
                    <option value="">-- Seleziona fornitore esistente --</option>
                    {suppliers.map(s => (
                      <option key={s.supplier_id} value={s.supplier_id}>
                        {s.name} - {s.vat_number}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Supplier Info */}
              <div className="grid grid-cols-3 gap-4">
                <div className="form-group">
                  <label className="form-label">Nome Fornitore *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.supplier_name}
                    onChange={(e) => setFormData({...formData, supplier_name: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email Fornitore *</label>
                  <input
                    type="email"
                    className="form-input"
                    value={formData.supplier_email}
                    onChange={(e) => setFormData({...formData, supplier_email: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Telefono</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={formData.supplier_phone}
                    onChange={(e) => setFormData({...formData, supplier_phone: e.target.value})}
                  />
                </div>
              </div>

              {/* Items */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-primary uppercase text-sm font-bold flex items-center gap-2">
                    <Barcode className="w-4 h-4" />
                    Articoli da Ordinare
                  </h3>
                  <Button type="button" variant="outline" size="sm" onClick={addItem} className="border-primary text-primary">
                    <Plus className="w-4 h-4 mr-1" /> Aggiungi
                  </Button>
                </div>
                <div className="space-y-2">
                  {formData.items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-end bg-zinc-950/50 p-3 rounded-sm">
                      <div className="col-span-2 form-group">
                        <label className="form-label text-xs">Codice</label>
                        <input
                          type="text"
                          className="form-input uppercase"
                          value={item.item_code}
                          onChange={(e) => updateItem(index, "item_code", e.target.value.toUpperCase())}
                          placeholder="COD001"
                        />
                      </div>
                      <div className="col-span-5 form-group">
                        <label className="form-label text-xs">Descrizione</label>
                        <input
                          type="text"
                          className="form-input"
                          value={item.description}
                          onChange={(e) => updateItem(index, "description", e.target.value)}
                          placeholder="Descrizione articolo"
                          required
                        />
                      </div>
                      <div className="col-span-2 form-group">
                        <label className="form-label text-xs">Marca</label>
                        <input
                          type="text"
                          className="form-input"
                          value={item.brand}
                          onChange={(e) => updateItem(index, "brand", e.target.value)}
                        />
                      </div>
                      <div className="col-span-2 form-group">
                        <label className="form-label text-xs">Quantità</label>
                        <input
                          type="number"
                          className="form-input"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, "quantity", parseInt(e.target.value) || 1)}
                          min="1"
                          required
                        />
                      </div>
                      <div className="col-span-1 flex items-center justify-center">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(index)}
                          className="text-red-400 hover:text-red-300"
                          disabled={formData.items.length <= 1}
                        >
                          ✕
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div className="form-group">
                <label className="form-label">Note</label>
                <textarea
                  className="form-textarea h-20"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Note per il fornitore..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="ghost" onClick={() => setShowDialog(false)} className="text-zinc-400">
                  Annulla
                </Button>
                <Button 
                  data-testid="submit-order-btn" 
                  type="submit" 
                  className="btn-primary"
                  disabled={formData.items.length === 0 || !formData.supplier_name}
                >
                  Crea Ordine
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Orders List - Table View */}
      {orders.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800 text-left text-xs text-zinc-500 uppercase">
                <th className="py-3 px-4 font-medium">N°</th>
                <th className="py-3 px-4 font-medium">Stato</th>
                <th className="py-3 px-4 font-medium">Fornitore</th>
                <th className="py-3 px-4 font-medium text-center">Articoli</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr 
                  key={order.order_id}
                  className="border-b border-zinc-800/50 hover:bg-zinc-900/50 cursor-pointer transition-colors"
                  onClick={() => setSelectedOrder(order)}
                >
                  <td className="py-3 px-4">
                    <span className="text-primary font-bold">#{order.order_number}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-0.5 rounded text-xs uppercase ${statusColors[order.status]}`}>
                      {statusLabels[order.status]}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-white font-medium">{order.supplier_name}</span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className="text-zinc-400">{order.items.length}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <Package className="w-16 h-16 empty-state-icon" />
          <p className="empty-state-title">Nessun ordine</p>
          <p className="empty-state-text">Crea un ordine fornitore</p>
        </div>
      )}

      {/* Detail Dialog */}
      {selectedOrder && (
        <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white uppercase font-['Barlow_Condensed'] text-xl">
                Ordine Fornitore #{selectedOrder.order_number}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6 mt-4">
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                  <p className="text-white font-medium text-lg">{selectedOrder.supplier_name}</p>
                  <p className="text-zinc-500">{selectedOrder.supplier_email}</p>
                  {selectedOrder.supplier_phone && (
                    <p className="text-zinc-500">{selectedOrder.supplier_phone}</p>
                  )}
                </div>
                <Select
                  value={selectedOrder.status}
                  onValueChange={(value) => handleStatusChange(selectedOrder.order_id, value)}
                >
                  <SelectTrigger className="w-40 bg-zinc-950 border-zinc-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="draft">Bozza</SelectItem>
                    <SelectItem value="sent">Inviato</SelectItem>
                    <SelectItem value="confirmed">Confermato</SelectItem>
                    <SelectItem value="in_delivery">In Consegna</SelectItem>
                    <SelectItem value="shipped">Spedito</SelectItem>
                    <SelectItem value="received">Ricevuto</SelectItem>
                    <SelectItem value="cancelled">Annullato</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  onClick={() => sendViaEmail(selectedOrder)}
                  className="border-blue-500 text-blue-500 hover:bg-blue-500/10"
                  data-testid="order-send-email-btn"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Email
                </Button>
                <Button
                  variant="outline"
                  onClick={() => sendViaWhatsApp(selectedOrder)}
                  className="border-green-500 text-green-500 hover:bg-green-500/10"
                  data-testid="order-send-whatsapp-btn"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp
                </Button>
                <Button
                  variant="outline"
                  onClick={() => exportToPDF(selectedOrder)}
                  className="border-primary text-primary hover:bg-primary/10"
                  data-testid="order-export-pdf-btn"
                >
                  <Download className="w-5 h-5 mr-2" />
                  PDF
                </Button>
              </div>

              {/* Ricambista Status Info */}
              {selectedOrder.ricambista_status && (
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg space-y-2">
                  <div className="flex items-center gap-2">
                    <Truck className="w-5 h-5 text-blue-400" />
                    <span className="text-white font-medium">Stato dal Fornitore</span>
                  </div>
                  <p className="text-blue-300">
                    {ricambistaStatusLabels[selectedOrder.ricambista_status] || selectedOrder.ricambista_status}
                  </p>
                  {selectedOrder.tracking_number && (
                    <p className="text-sm">
                      <span className="text-zinc-400">Tracking:</span>{" "}
                      <span className="text-white font-mono">{selectedOrder.tracking_number}</span>
                    </p>
                  )}
                  {selectedOrder.ricambista_notes && (
                    <p className="text-sm">
                      <span className="text-zinc-400">Note:</span>{" "}
                      <span className="text-zinc-300">{selectedOrder.ricambista_notes}</span>
                    </p>
                  )}
                  {selectedOrder.shipping_status && (
                    <p className="text-sm">
                      <span className="text-zinc-400">Spedizione:</span>{" "}
                      <span className={selectedOrder.shipping_status === "complete" ? "text-green-400" : "text-orange-400"}>
                        {selectedOrder.shipping_status === "complete" ? "Completa" : "Parziale"}
                      </span>
                    </p>
                  )}
                </div>
              )}

              {/* Items (no prices) */}
              <div className="overflow-x-auto">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-primary uppercase text-xs font-bold">Articoli</h4>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAddItem(true)}
                    className="border-primary text-primary hover:bg-primary/10"
                    data-testid="add-item-btn"
                  >
                    <PlusCircle className="w-4 h-4 mr-1" />
                    Aggiungi Articolo
                  </Button>
                </div>
                <table className="w-full data-table">
                  <thead>
                    <tr>
                      <th>Codice</th>
                      <th>Articolo</th>
                      <th>Marca</th>
                      <th className="text-center">Quantità</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedOrder.items.map((item, index) => (
                      <tr key={index}>
                        <td className="font-['JetBrains_Mono'] uppercase">{item.item_code || "-"}</td>
                        <td>{item.description}</td>
                        <td>{item.brand || "-"}</td>
                        <td className="text-center font-['JetBrains_Mono']">{item.quantity}</td>
                        <td className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveItem(index)}
                            className="text-red-500 hover:text-red-400 p-1 h-auto"
                            data-testid={`remove-item-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {selectedOrder.notes && (
                <div>
                  <h4 className="text-primary uppercase text-xs font-bold mb-2">Note</h4>
                  <p className="text-zinc-400">{selectedOrder.notes}</p>
                </div>
              )}

              {/* Delete Order Button */}
              <div className="border-t border-zinc-800 pt-4">
                <Button
                  variant="destructive"
                  onClick={() => setDeleteConfirm(selectedOrder)}
                  className="w-full"
                  data-testid="delete-order-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Elimina Ordine
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Add Manual Item Dialog */}
      {showAddItem && selectedOrder && (
        <Dialog open={showAddItem} onOpenChange={() => setShowAddItem(false)}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white uppercase font-['Barlow_Condensed']">
                Aggiungi Articolo
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="form-group">
                <label className="form-label">Codice *</label>
                <Input
                  value={newItem.item_code}
                  onChange={(e) => setNewItem({ ...newItem, item_code: e.target.value.toUpperCase() })}
                  placeholder="Es: 1K0615301M"
                  className="bg-zinc-950 uppercase"
                  data-testid="new-item-code"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Descrizione *</label>
                <Input
                  value={newItem.description}
                  onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                  placeholder="Descrizione articolo..."
                  className="bg-zinc-950"
                  data-testid="new-item-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Marca *</label>
                  <Input
                    value={newItem.brand}
                    onChange={(e) => setNewItem({ ...newItem, brand: e.target.value })}
                    placeholder="Es: Bosch"
                    className="bg-zinc-950"
                    data-testid="new-item-brand"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Quantità</label>
                  <Input
                    type="number"
                    min="1"
                    value={newItem.quantity}
                    onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 1 })}
                    className="bg-zinc-950"
                    data-testid="new-item-quantity"
                  />
                </div>
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button variant="ghost" onClick={() => setShowAddItem(false)}>
                Annulla
              </Button>
              <Button onClick={handleAddManualItem} data-testid="confirm-add-item">
                <PlusCircle className="w-4 h-4 mr-2" />
                Aggiungi
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-500 flex items-center gap-2">
                <Trash2 className="w-5 h-5" />
                Elimina Ordine
              </DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-3">
              <p className="text-zinc-300">
                Sei sicuro di voler eliminare questo ordine fornitore?
              </p>
              <div className="bg-zinc-950 p-3 rounded border border-zinc-800">
                <div className="text-primary font-['JetBrains_Mono'] text-sm">
                  Ordine #{deleteConfirm.order_number}
                </div>
                <div className="text-white mt-1">{deleteConfirm.supplier_name}</div>
                <div className="text-zinc-400 text-sm mt-1">
                  {deleteConfirm.items?.length || 0} articoli
                </div>
              </div>
              <p className="text-zinc-500 text-sm">
                Questa azione non può essere annullata.
              </p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
                Annulla
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleDeleteOrder(deleteConfirm.order_id)}
                data-testid="confirm-delete-order"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Elimina
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
